import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import ArrowDownIcon from "../assets/icons/arrow-down.svg";
import { useTheme } from "../context/ThemeContext";

type ResponsiveWidths = {
  [breakpoint: number]: { [colIndex: number]: string };
};

type ZebraColors = { odd: string; even: string };

type TableProps = {
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  columnWidths?: string[];
  responsiveWidths?: ResponsiveWidths;

  onDoubleClick?: (
    viewIndex: number,
    rowData: (string | number | React.ReactNode)[],
    originalIndex?: number
  ) => void;

  /** Seleção em grupo (ex.: Standby por OS/PV) */
  isTableStandby?: boolean;
  osColumnIndex?: number;

  /** Estilo por célula vindo da tela */
  enableCellColors?: boolean;
  getCellStyle?: (
    rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    originalIndex: number,
    rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => React.CSSProperties | undefined;

  /** NOVO: estilo por linha vindo da tela */
  getRowStyle?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => React.CSSProperties | undefined;

  enableZebra?: boolean;
  zebraColors?: ZebraColors;
  hoverHighlight?: boolean;
  dense?: boolean;
  softBorders?: boolean;
  stickyFirstCol?: boolean;

  /** Coluna “Dias de Atraso” (VIEW) */
  atrasoColumnIndex?: number;

  /** Paleta do atraso */
  atrasoColors?: {
    good?: string; // <=3
    warn?: string; // 4..9
    bad?: string; // >=10
  };

  /** Forma explícita de dizer se a linha é pendente (amarela) */
  isRowPending?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => boolean;

  /**
   * ESCOPO da cor de atraso:
   * - "cell": pinta somente a célula da coluna de atraso (padrão)
   * - "row": pinta a linha inteira (opcional)
   */
  delayScope?: "cell" | "row";

  /**
   * Agrupamento de linhas em “seções” com barras horizontais
   * Ex.: "mine" | "others" → "Minhas O.S." / "O.S. de outros coordenadores"
   */
  getRowGroup?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => string | null;

  /** Labels a exibir para cada chave de grupo */
  groupLabels?: { [groupKey: string]: string };
};

type SortConfig = { index: number | null; direction: "asc" | "desc" };

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  columnWidths = [],
  responsiveWidths = {},
  onDoubleClick,

  isTableStandby = false,
  osColumnIndex = 0,

  enableCellColors = false,
  getCellStyle,
  getRowStyle,

  enableZebra = true,
  zebraColors = { odd: "#f6f8fb", even: "#fbfcfe" },
  hoverHighlight = true,
  dense = false,
  softBorders = false,
  stickyFirstCol = false,

  atrasoColumnIndex,
  atrasoColors,

  isRowPending,
  delayScope = "cell",

  getRowGroup,
  groupLabels,
}) => {
  const [filters, setFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );
  const [selectedViewIndex, setSelectedViewIndex] = useState<number | null>(
    null
  );
  const [selectedOS, setSelectedOS] = useState<string | null>(null);
  const [activeWidths, setActiveWidths] = useState<string[]>(columnWidths);
  const [fontSize, setFontSize] = useState("14px");
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [sort, setSort] = useState<SortConfig>({
    index: null,
    direction: "asc",
  });
  const tableRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();
  const headerPrimary = theme?.primary ?? "#1f290c";
  const optionSelected = theme?.table_option_selected ?? "#D3E1F0";
  const optionHover = theme?.table_option_hover ?? "#D3E1F0";

  const borderColor = softBorders ? "#e5e7eb" : "black";

  const PENDING_BASE = "#FFF9D6";
  const SELECTED_BLUE = optionSelected;
  const PENDING_SELECTED_SOLID = "#F3E1A5B8";

  const ROW_HOVER_BG = optionHover;

  const atrasoGood = atrasoColors?.good ?? "#DCFCE7";
  const atrasoWarn = atrasoColors?.warn ?? "#F0BF8A";
  const atrasoBad = atrasoColors?.bad ?? "#C44949";

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      const sorted = Object.keys(responsiveWidths)
        .map(Number)
        .sort((a, b) => b - a);
      const bp = sorted.find((x) => width <= x) ?? null;

      if (bp) {
        const conf = responsiveWidths[bp];
        const w = headers.map(
          (_, i) => conf[i + 1] || columnWidths[i] || "auto"
        );
        setActiveWidths(w);
      } else {
        setActiveWidths(columnWidths);
      }

      const clamped = Math.max(12, Math.min(18, width / 120));
      setFontSize(clamped + "px");
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [responsiveWidths, headers, columnWidths]);

  const parseMaybeNumber = (v: string): number | null => {
    const n = Number(
      v
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".")
    );
    return Number.isFinite(n) ? n : null;
  };

  const extractOS = (cell: string | number | React.ReactNode): string => {
    if (typeof cell === "string" || typeof cell === "number") {
      return String(cell);
    }

    if (
      cell &&
      typeof cell === "object" &&
      "props" in (cell as any) &&
      (cell as any).props?.os
    ) {
      return String((cell as any).props.os);
    }

    return "";
  };

  const getComparable = (cell: string | number | React.ReactNode) => {
    if (typeof cell === "number") return { kind: "num" as const, val: cell };

    if (typeof cell === "string") {
      const maybe = parseMaybeNumber(cell);
      if (maybe !== null) return { kind: "num" as const, val: maybe };
      return { kind: "str" as const, val: cell };
    }

    return { kind: "str" as const, val: "" };
  };

  const rowBaseBg = (i: number) =>
    enableZebra
      ? i % 2 === 0
        ? zebraColors.odd
        : zebraColors.even
      : "#ffffff";

  const { visibleRows, viewToOriginal } = useMemo(() => {
    const filtered: {
      row: (string | number | React.ReactNode)[];
      original: number;
    }[] = [];

    rows.forEach((row, originalIdx) => {
      const passes = row.every((cell, i) => {
        const f = (filters[i] ?? "").toLowerCase();
        if (!f) return true;

        const v =
          typeof cell === "string" || typeof cell === "number"
            ? String(cell).toLowerCase()
            : "";

        return v.includes(f);
      });

      if (passes) filtered.push({ row, original: originalIdx });
    });

    if (sort.index != null) {
      const i = sort.index;
      const m = sort.direction === "asc" ? 1 : -1;

      filtered.sort((a, b) => {
        const A = getComparable(a.row[i]);
        const B = getComparable(b.row[i]);

        if (A.kind === "num" && B.kind === "num") {
          if (A.val < B.val) return -1 * m;
          if (A.val > B.val) return 1 * m;
          return 0;
        }

        return (
          String(A.val).localeCompare(String(B.val), "pt-BR", {
            sensitivity: "base",
            numeric: true,
          }) * m
        );
      });
    }

    return {
      visibleRows: filtered.map((x) => x.row),
      viewToOriginal: filtered.map((x) => x.original),
    };
  }, [rows, filters, sort]);

  const selectRow = useCallback(
    (index: number | null) => {
      if (index == null || index < 0 || index >= visibleRows.length) return;

      const linha = visibleRows[index];

      if (isTableStandby) {
        const raw = linha[osColumnIndex];
        const osValue = extractOS(raw);
        setSelectedOS(osValue || null);
        setSelectedViewIndex(null);
      } else {
        setSelectedViewIndex(index);
        setSelectedOS(null);
      }

      const root = tableRef.current;
      if (root) {
        const trs = root.querySelectorAll("tbody tr");
        if (trs[index]) {
          (trs[index] as HTMLElement).scrollIntoView({ block: "nearest" });
        }
      }
    },
    [visibleRows, isTableStandby, osColumnIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();

      if (!visibleRows.length) return;

      let current =
        isTableStandby && selectedOS
          ? visibleRows.findIndex(
              (r) => extractOS(r[osColumnIndex]) === selectedOS
            )
          : selectedViewIndex ?? -1;

      if (e.key === "ArrowDown") {
        current = Math.min(visibleRows.length - 1, current + 1);
      } else {
        current = Math.max(0, current - 1);
      }

      selectRow(current);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visibleRows,
    selectedOS,
    selectedViewIndex,
    selectRow,
    isTableStandby,
    osColumnIndex,
  ]);

  const colorForDelay = (value: string | number | React.ReactNode) => {
    let n: number | null = null;

    if (typeof value === "number") n = Number.isFinite(value) ? value : null;
    else if (typeof value === "string") n = parseMaybeNumber(value);

    if (n == null) return null;

    let bg = atrasoBad;
    if (n <= 3) bg = atrasoGood;
    else if (n <= 9) bg = atrasoWarn;

    const fg = bg === atrasoBad ? "#fff" : "#111";
    return { bg, fg, n };
  };

  const looksLikeYellow = (c?: string) => {
    if (!c) return false;
    const x = c.toLowerCase().replace(/\s+/g, "");
    return (
      x.includes("#fff9d6") ||
      x.includes("#fffac8") ||
      x.includes("ffe79a") ||
      x.includes("fff7ed") ||
      x.includes("fffbeb")
    );
  };

  const isPendingRowComputed = (
    linha: (string | number | React.ReactNode)[],
    vIndex: number,
    oIndex: number
  ) => {
    if (isRowPending) return !!isRowPending(linha, vIndex, oIndex);
    if ((linha as any)?.__pendente_retorno === true) return true;

    if (enableCellColors && getCellStyle) {
      for (let col = 0; col < headers.length; col++) {
        const extra =
          getCellStyle(vIndex, col, linha[col], oIndex, linha, headers[col]) ||
          {};
        const bg =
          (extra.backgroundColor as string) || (extra.background as string);
        if (looksLikeYellow(bg)) return true;
      }
    }

    return false;
  };

  const headerHeight = dense ? "36px" : "40px";
  const filterHeight = dense ? "30px" : "35px";
  const cellHeight = dense ? "32px" : "35px";
  const cellPadding = dense ? "0 4px" : "0 5px";

  const activeColSpan = useMemo(
    () =>
      headers.reduce(
        (acc, _h, i) => (activeWidths[i] === "0px" ? acc : acc + 1),
        0
      ),
    [headers, activeWidths]
  );

  return (
    <div ref={tableRef} style={{ width: "100%", overflowX: "auto" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <thead>
          <tr>
            {headers.map((header, index) =>
              activeWidths[index] !== "0px" ? (
                <th
                  key={index}
                  onClick={() =>
                    setSort((prev) =>
                      prev.index === index
                        ? {
                            index,
                            direction:
                              prev.direction === "asc" ? "desc" : "asc",
                          }
                        : { index, direction: "asc" }
                    )
                  }
                  style={{
                    width: activeWidths[index],
                    backgroundColor: headerPrimary,
                    color: "#ffffff",
                    fontWeight: 300,
                    fontSize,
                    height: headerHeight,
                    padding: "0 8px",
                    border: `1px solid ${borderColor}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  role="button"
                  aria-sort={
                    sort.index === index
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  title="Clique para ordenar"
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span>{header}</span>
                    {sort.index === index && (
                      <img
                        src={ArrowDownIcon}
                        alt="ordenar"
                        style={{
                          width: 18,
                          height: 18,
                          marginLeft: 6,
                          transform:
                            sort.direction === "asc"
                              ? "rotate(180deg)"
                              : "none",
                          transition: "transform 120ms ease",
                        }}
                      />
                    )}
                  </div>
                </th>
              ) : null
            )}
          </tr>
        </thead>

        <tbody>
          <tr>
            {headers.map((_, index) =>
              activeWidths[index] !== "0px" ? (
                <td
                  key={`filter-${index}`}
                  style={{
                    width: activeWidths[index],
                    height: filterHeight,
                    border: `1px solid ${borderColor}`,
                    padding: "0 8px",
                    background: "#f8fafc",
                    position:
                      index === 0 && stickyFirstCol ? "sticky" : undefined,
                    left: index === 0 && stickyFirstCol ? 0 : undefined,
                    zIndex: index === 0 && stickyFirstCol ? 1 : undefined,
                  }}
                >
                  <input
                    type="text"
                    value={filters[index]}
                    onChange={(e) => {
                      const nf = [...filters];
                      nf[index] = e.target.value;
                      setFilters(nf);
                      setSelectedOS(null);
                      setSelectedViewIndex(null);
                    }}
                    style={{
                      width: "100%",
                      height: dense ? "24px" : "28px",
                      border: "none",
                      outline: "none",
                      fontSize,
                      textAlign: "center",
                      textTransform: "uppercase",
                      background: "transparent",
                    }}
                  />
                </td>
              ) : null
            )}
          </tr>

          {visibleRows.length > 0 &&
            (() => {
              const result: React.ReactNode[] = [];
              let lastGroupKey: string | null = null;

              visibleRows.forEach((linha, viewIndex) => {
                const originalIndex = viewToOriginal[viewIndex];

                const osValue = isTableStandby
                  ? extractOS(linha[osColumnIndex]) || null
                  : null;

                const pendente = isPendingRowComputed(
                  linha,
                  viewIndex,
                  originalIndex
                );

                const isSelected = isTableStandby
                  ? selectedOS != null && osValue === selectedOS
                  : selectedViewIndex === viewIndex;

                const base = pendente ? PENDING_BASE : rowBaseBg(viewIndex);
                const baseOrHover =
                  hoverRow === viewIndex && !isSelected ? ROW_HOVER_BG : base;

                const rowBaseLayer = `linear-gradient(0deg, ${baseOrHover}, ${baseOrHover})`;

                const selectionLayer = isSelected
                  ? `linear-gradient(0deg, ${
                      pendente ? PENDING_SELECTED_SOLID : SELECTED_BLUE
                    }, ${pendente ? PENDING_SELECTED_SOLID : SELECTED_BLUE})`
                  : "";

                const rowDelayLayer =
                  delayScope === "row" && typeof atrasoColumnIndex === "number"
                    ? (() => {
                        const val = linha[atrasoColumnIndex];
                        const info = colorForDelay(val);
                        return info
                          ? `linear-gradient(0deg, ${info.bg}, ${info.bg})`
                          : "";
                      })()
                    : "";

                const rowLayers: string[] = [];
                if (rowDelayLayer) rowLayers.push(rowDelayLayer);
                if (selectionLayer) rowLayers.push(selectionLayer);
                rowLayers.push(rowBaseLayer);

                const groupKey =
                  getRowGroup?.(linha, viewIndex, originalIndex) || null;

                const showGroupBar =
                  groupKey &&
                  groupLabels &&
                  groupLabels[groupKey] &&
                  groupKey !== lastGroupKey;

                if (showGroupBar) {
                  lastGroupKey = groupKey;
                  result.push(
                    <tr key={`group-${groupKey}-${viewIndex}`}>
                      <td
                        colSpan={activeColSpan || headers.length}
                        style={{
                          padding: "6px 10px",
                          background: "#264a7d",
                          color: "#ffffff",
                          fontWeight: 300,
                          fontSize,
                          textAlign: "left",
                          letterSpacing: "0.03em",
                          borderTop: "1px solid black",
                          borderBottom: "1px solid black",
                          borderLeft: "1px solid black",
                          borderRight: "1px solid black",
                        }}
                      >
                        {groupLabels[groupKey]}
                      </td>
                    </tr>
                  );
                }

                const externalRowStyle = getRowStyle?.(
                  linha,
                  viewIndex,
                  originalIndex
                );

                const externalRowBg =
                  (externalRowStyle?.backgroundColor as string) ||
                  (externalRowStyle?.background as string) ||
                  null;

                const rowLayersWithExternal = [...rowLayers];
                if (externalRowBg) {
                  rowLayersWithExternal.unshift(
                    `linear-gradient(0deg, ${externalRowBg}, ${externalRowBg})`
                  );
                }

                const sanitizedExternalRowStyle = externalRowStyle
                  ? { ...externalRowStyle }
                  : undefined;

                if (sanitizedExternalRowStyle) {
                  delete sanitizedExternalRowStyle.background;
                  delete sanitizedExternalRowStyle.backgroundColor;
                }

                result.push(
                  <tr
                    key={viewIndex}
                    onClick={() => selectRow(viewIndex)}
                    onDoubleClick={() =>
                      onDoubleClick?.(viewIndex, linha, originalIndex)
                    }
                    onMouseEnter={() =>
                      hoverHighlight && setHoverRow(viewIndex)
                    }
                    onMouseLeave={() => hoverHighlight && setHoverRow(null)}
                    style={{
                      cursor: "pointer",
                      backgroundImage: rowLayersWithExternal.join(", "),
                      backgroundOrigin: "padding-box",
                      backgroundClip: "padding-box",
                      ...sanitizedExternalRowStyle,
                    }}
                  >
                    {headers.map((headerTitle, colIndex) => {
                      if (activeWidths[colIndex] === "0px") return null;

                      const extra =
                        enableCellColors && getCellStyle
                          ? getCellStyle(
                              viewIndex,
                              colIndex,
                              linha[colIndex],
                              originalIndex,
                              linha,
                              headerTitle
                            ) || {}
                          : {};

                      let extraBg: string | null = null;
                      if (extra.backgroundColor) {
                        extraBg = String(extra.backgroundColor);
                      } else if (extra.background) {
                        extraBg = String(extra.background);
                      }

                      const extraLayer = extraBg
                        ? `linear-gradient(0deg, ${extraBg}, ${extraBg})`
                        : "";

                      const isDelayCell =
                        typeof atrasoColumnIndex === "number" &&
                        colIndex === atrasoColumnIndex;

                      const delayInfo = isDelayCell
                        ? colorForDelay(linha[colIndex])
                        : null;

                      const layersCell: string[] = [];

                      if (delayInfo && delayScope === "cell") {
                        layersCell.push(
                          `linear-gradient(0deg, ${delayInfo.bg}, ${delayInfo.bg})`
                        );
                      }

                      if (extraLayer) layersCell.push(extraLayer);
                      layersCell.push(...rowLayersWithExternal);

                      const fgColor =
                        delayInfo && delayScope === "cell"
                          ? delayInfo.fg
                          : undefined;

                      const sanitizedCellStyle = { ...extra };
                      delete (sanitizedCellStyle as any).background;
                      delete (sanitizedCellStyle as any).backgroundColor;

                      return (
                        <td
                          key={colIndex}
                          style={{
                            width: activeWidths[colIndex],
                            height: cellHeight,
                            border: `1px solid ${borderColor}`,
                            fontSize,
                            fontWeight: 300,
                            textAlign: "center",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            padding: cellPadding,
                            position:
                              stickyFirstCol && colIndex === 0
                                ? "sticky"
                                : undefined,
                            left:
                              stickyFirstCol && colIndex === 0 ? 0 : undefined,
                            zIndex:
                              stickyFirstCol && colIndex === 0 ? 1 : undefined,
                            backgroundImage: layersCell.join(", "),
                            backgroundOrigin: "padding-box",
                            backgroundClip: "padding-box",
                            color: fgColor,
                            ...sanitizedCellStyle,
                          }}
                        >
                          {linha[colIndex]}
                        </td>
                      );
                    })}
                  </tr>
                );
              });

              return result;
            })()}

          {!visibleRows.length && (
            <tr>
              <td
                colSpan={headers.length}
                style={{
                  padding: 16,
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize,
                  border: `1px solid ${borderColor}`,
                  background: "#fff",
                }}
              >
                Nenhum registro encontrado com os filtros atuais.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
